import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { AlertTriangle, AlertCircle, Info, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

const PredictiveAlertsPage = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/predictive-alerts');
      setAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      toast.error('Failed to load predictive alerts');
    } finally {
      setLoading(false);
    }
  };

  const getAlertIcon = (level) => {
    switch (level) {
      case 'critical':
        return { Icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-100', border: 'border-red-200' };
      case 'high':
        return { Icon: AlertCircle, color: 'text-orange-600', bg: 'bg-orange-100', border: 'border-orange-200' };
      case 'medium':
        return { Icon: Info, color: 'text-yellow-600', bg: 'bg-yellow-100', border: 'border-yellow-200' };
      default:
        return { Icon: Info, color: 'text-blue-600', bg: 'bg-blue-100', border: 'border-blue-200' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="predictive-alerts-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">Predictive Failure Alerts</h1>
        <p className="text-lg text-muted-foreground">
          AI-powered insights based on QC history - {alerts.length} asset(s) require attention
        </p>
      </div>

      {alerts.length === 0 ? (
        <div className="bg-white rounded-xl border border-border shadow-sm p-12 text-center">
          <TrendingDown className="w-16 h-16 mx-auto mb-4 text-green-600" />
          <h3 className="text-2xl font-bold mb-2">All Clear!</h3>
          <p className="text-xl text-muted-foreground">No predictive alerts at this time</p>
          <p className="text-sm text-muted-foreground mt-2">
            The system is monitoring all assets for potential issues
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {alerts.map((alert) => {
            const alertStyle = getAlertIcon(alert.alert_level);
            const Icon = alertStyle.Icon;

            return (
              <div
                key={alert.asset_id}
                data-testid={`alert-${alert.asset_id}`}
                onClick={() => navigate(`/history?search=${alert.asset_id}`)}
                className={`bg-white rounded-xl border ${alertStyle.border} shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer`}
              >
                <div className="flex items-start gap-4">
                  {/* Alert Icon */}
                  <div className={`w-12 h-12 rounded-full ${alertStyle.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-6 h-6 ${alertStyle.color}`} />
                  </div>

                  {/* Alert Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-xl font-bold mb-1">{alert.asset_id}</h3>
                        <p className="text-sm text-muted-foreground">
                          {alert.brand} {alert.model} ({alert.asset_type})
                        </p>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${alertStyle.bg} ${alertStyle.color}`}
                      >
                        {alert.alert_level}
                      </span>
                    </div>

                    <p className={`font-medium mb-4 ${alertStyle.color}`}>{alert.alert_message}</p>

                    {/* Statistics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted rounded-lg">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Total QCs</p>
                        <p className="text-lg font-bold">{alert.total_qcs}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Hold Count</p>
                        <p className="text-lg font-bold text-red-600">{alert.hold_count}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Hold Rate</p>
                        <p className="text-lg font-bold text-red-600">{alert.hold_rate}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Recent Avg Score</p>
                        <p className="text-lg font-bold">{alert.avg_recent_score}</p>
                      </div>
                    </div>

                    {/* Recurring Issues */}
                    {Object.keys(alert.recurring_issues).length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-bold mb-2">Recurring Issues:</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(alert.recurring_issues).map(([issue, count]) => (
                            <span
                              key={issue}
                              className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium"
                            >
                              {issue} ({count}x)
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info Panel */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-bold mb-4 text-blue-900">How Predictive Alerts Work</h3>
        <ul className="space-y-2 text-sm text-blue-900">
          <li className="flex items-start">
            <div className="w-2 h-2 rounded-full bg-blue-600 mr-3 mt-1.5"></div>
            <span><strong>Critical:</strong> Asset has >60% failure rate in recent QCs</span>
          </li>
          <li className="flex items-start">
            <div className="w-2 h-2 rounded-full bg-blue-600 mr-3 mt-1.5"></div>
            <span><strong>High:</strong> Recent QC scores below 70 (quality declining)</span>
          </li>
          <li className="flex items-start">
            <div className="w-2 h-2 rounded-full bg-blue-600 mr-3 mt-1.5"></div>
            <span><strong>Medium:</strong> Declining performance trend or recurring issues detected</span>
          </li>
          <li className="flex items-start">
            <div className="w-2 h-2 rounded-full bg-blue-600 mr-3 mt-1.5"></div>
            <span>Click on any alert to view full asset history and take action</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PredictiveAlertsPage;
